/**
 * Dropdown
 * Handles dropdown functions
 * Improved UX
 * Improved accessibility
 * @module dropdown
 */
import $ from 'jquery';
import throttle from 'lodash/throttle';
import Keyboard from './keyboard';
import registerJQueryPlugin from '../lib/register-jquery-plugin';
import debounce from 'lodash/debounce';

export const PLUGIN_NAME = 'dropdown';

export const DD_CLASS = '.js-dropdown';
export const DD_MENU_CLASS = '.js-dropdown__menu';
export const DD_TRIGGER_CLASS = '.js-dropdown__trigger';
export const DD_OPEN_STATE = 'dd-is-open';
export const DD_DISABLED_STATE = 'is-disabled';


export const DEFAULTS = {
  animationSpeed: 0,
  wait: 100,
  returnFocus: null,
  offsetContent: false,
};
import mergeOptions from '../lib/merge-options';


const makeConditionalDebounce = (fn, wait, shouldSkip) => {
  const debounced = debounce(fn, wait);
  const wrapper = function (...args) {
    if (shouldSkip()) {
      debounced.cancel();           
      return fn.apply(this, args); 
    }
    return debounced.apply(this, args);
  };
  wrapper.cancel = debounced.cancel.bind(debounced);
  wrapper.flush  = (debounced.flush || (() => {})).bind(debounced);
  return wrapper;
};


/**
 * Set Open State
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 */
const setOpenState = ($el, $ddTrigger, $ddMenu) => {
  $el.addClass(DD_OPEN_STATE);
  $ddTrigger.addClass(DD_OPEN_STATE);
  $ddMenu.addClass(DD_OPEN_STATE);
  $el.trigger('dropdown.open');
};
/**
 * Remove Open State
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 */
const removeOpenState = ($el, $ddTrigger, $ddMenu) => {
  $el.removeClass(DD_OPEN_STATE);
  $ddTrigger.removeClass(DD_OPEN_STATE);
  $ddMenu.removeClass(DD_OPEN_STATE);
};
/**
 * Set Aria Expanded
 * @param  {jQuery} $el - dropdown trigger element
 * @param  {boolean} $ddMenu - dropdown content element
 */
const ariaExpanded = ($el, bool) => {
  $el.attr('aria-expanded', bool);
};
/**
 * Release Trap Focus
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $returnFocus - returnFocus
 */
const releaseTrapFocus = ($el, $returnFocus) => {
  if ($el.hasClass('trapped')) {
    $el.removeClass('trapped');
    if($returnFocus) {
      Keyboard.releaseFocus($el, $returnFocus);
    } else {
      Keyboard.releaseFocus($el);
    }
  }
};
/**
 * Close Dropdown
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 * @param  {lodash} openDebounce - debounce for opening dropdown
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const closeDropdown = ($el, $ddTrigger, $ddMenu, openDebounce, opts) => {
   /* This setTimeout is use for IE 11 focus issue. This is allows focus flow
    * to happen naturally. It seems like without the setTimeout the focus order
    * gets thrown off.
    */
  setTimeout(()=>{
    // Cancel the trailing debounced invocation.
    openDebounce.cancel();
    removeOpenState($el, $ddTrigger, $ddMenu);
    $ddMenu.stop().animate({
      opacity: 0,
    }, opts.animationSpeed / 2, () => {
      ariaExpanded($ddTrigger, false);
      $ddMenu.css('display', 'none');
    });
  }, 1);
};
/**
 * Focus on dropdown trigger
 * @param  {jQuery} $el - dropdown wrap
 */
const focusOnTrigger = ($el) => {
  $el.find(DD_TRIGGER_CLASS).first().focus();
}
/**
 * Top Level Menu Items Handler
 * @param  {event} evt - key event
 * @param  {boolean} isTrigger - if element is a trigger
 * @param  {jQuery} $targetMenu - targeted dropdown
 * @param  {jQuery} $siblingsMenu - Siblings Menu
 * @param  {string} direction - prev or next
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 * @param  {lodash} openDebounce - debounce for opening dropdown
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const topLevelMenuItemHandler = (evt, $targetMenu, $siblingsMenu, direction, $el, $ddTrigger, $ddMenu, openDebounce, opts) => {
  let $newTargetMenu = $targetMenu;
  if(!$targetMenu.length) {
    if(direction == 'next') {
      $newTargetMenu = $siblingsMenu.first();
    } else {
      $newTargetMenu = $siblingsMenu.last();
    }
  }
  closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts);
  focusOnTrigger($newTargetMenu);
}
/**
 * Arrow Handler
 * @param  {event} evt - key event
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 * @param  {lodash} openDebounce - debounce for opening dropdown
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @param  {jQuery} $focusable - dropdown focusable elements
 * @param  {jQuery} $firstFocusable - first focusable element
 * @param  {string} direction - prev or next
 */
 const arrowHandler = (evt ,$el, $ddTrigger, $ddMenu, openDebounce, opts, $focusable, $firstFocusable, direction) => {
  if(isNav) {
    evt.preventDefault();
    evt.stopPropagation();
  }

   const $siblingsMenu = $el.siblings(DD_CLASS);
   const isNav = $siblingsMenu.length > 1;
   const $currentEl = $el.find(':focus');
   const isTrigger = $currentEl.hasClass(DD_TRIGGER_CLASS.replace('.',''));
   const isArrowRight = Keyboard.parseKey(evt) === 'ARROWRIGHT';
   const isArrowDown = Keyboard.parseKey(evt) === 'ARROWDOWN';
   const isArrowLeft = Keyboard.parseKey(evt) === 'ARROWLEFT';
   const focusList =  [...$focusable];
   const currentIndex = focusList.indexOf($currentEl[0]);
   const targetIndex = direction == "next" ? currentIndex + 1 : currentIndex - 1;
   const $targetFocusableEl = focusList[targetIndex];
   const $targetMenu = direction == "next" ? $el.next(DD_CLASS) : $el.prev(DD_CLASS);
   const isTargetMenu = $targetMenu.length;
   const noTargetMenuAndItem = !$targetMenu.length && !$targetFocusableEl;

   if(isNav) {
     if(isTrigger) {
       if(isArrowRight || isArrowLeft) {
         topLevelMenuItemHandler(evt, $targetMenu, $siblingsMenu, direction, $el, $ddTrigger, $ddMenu, openDebounce, opts);
       }
     }
     if($targetFocusableEl) {
       if(!isTrigger || isArrowDown) {
         $targetFocusableEl.focus();
       }
     }
     if(!isTrigger && !$targetFocusableEl){
       closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts);
       if(isTargetMenu) {
         focusOnTrigger($targetMenu);
       }
       if(noTargetMenuAndItem){
         focusOnTrigger($siblingsMenu.first());
       }
     }
   }
 }
/**
 * Keydown Handler
 * @param  {event} evt - key event
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 * @param  {lodash} openDebounce - debounce for opening dropdown
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const keydownHandler = (evt , $el, $ddTrigger, $ddMenu, openDebounce, opts) => {
  const $focusable = Keyboard.findFocusable($el);
  const $firstFocusable = $focusable.eq(0);
  const $lastFocusable = $focusable.eq(-1);
  const isTab = Keyboard.parseKey(evt) === 'TAB';
  const isShiftTab = Keyboard.parseKey(evt) === 'SHIFT_TAB';
  const isEnter = Keyboard.parseKey(evt) === 'ENTER';
  const isSpace = Keyboard.parseKey(evt) === 'SPACE';
  const isClick = evt.type === "click";
  const isEscape = Keyboard.parseKey(evt) === Keyboard.keys.ESCAPE;
  const isNextArrows = Keyboard.parseKey(evt) === 'ARROWRIGHT' || Keyboard.parseKey(evt) === 'ARROWDOWN';
  const isPrevArrows = Keyboard.parseKey(evt) === 'ARROWLEFT' || Keyboard.parseKey(evt) === 'ARROWUP';
  const isFirstItem = evt.target === $firstFocusable[0];
  const isLastItem = evt.target === $lastFocusable[0];
  const $returnFocus = $el.data('returnFocus');
  
  // prevent default for enter and space when trigger element is focused
  if((isEnter || isSpace) && evt.target === $ddTrigger[0]) {
    evt.preventDefault();
  }
  if (isEscape) {
    evt.stopPropagation();
    // Close current dropdown
    closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts);
    if ($el.hasClass('trapped')) {
        releaseTrapFocus($el, $returnFocus);
    } else {
      focusOnTrigger($el);
    }
    // Close new dropdown because of new focus
    closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts);
  }
  if ((isEnter || isClick || isSpace) && evt.target === $ddTrigger[0]) {
    if ($el.hasClass('dropdown') && !$el.hasClass('dd-is-open')) {
      openDebounceHandler($el, $ddTrigger, $ddMenu, opts);
    } else if($el.hasClass('dropdown') && $el.hasClass('dd-is-open')) {
      closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts)
    }
    //use case for when enter key is used for popover
    if ($el.hasClass('popover') && !$el.hasClass('dd-is-open')) {
      openDebounceHandler($el, $ddTrigger, $ddMenu, opts);
    } else if($el.hasClass('popover') && $el.hasClass('dd-is-open')) {
      closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts)
    }
  }
  if (!$el.hasClass('trapped')) {
    if (isLastItem && isTab || isFirstItem && isShiftTab) {
      closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts)
    }
  }
  if (isNextArrows) {
    arrowHandler(evt ,$el, $ddTrigger, $ddMenu, openDebounce, opts, $focusable, $firstFocusable, 'next');
  }
  if (isPrevArrows) {
    arrowHandler(evt ,$el, $ddTrigger, $ddMenu, openDebounce, opts, $focusable, $firstFocusable, 'prev');
  }

};
/**
 * Open Dropdown
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const openDropdown = ($el, $ddTrigger, $ddMenu, opts) => {
  if ($ddMenu.hasClass(DD_OPEN_STATE)) {
    return;
  }
  lazyLoadContent($el);
  setOpenState($el, $ddTrigger, $ddMenu);

  $ddMenu.css({ display: 'block', opacity: 1 });
  ariaExpanded($ddTrigger, true);
};

/**
 * Lazy Load Content within Dropdown
 * @param  {jQuery} $el - dropdown wrap
 */
const lazyLoadContent = $el => {
  if ($el.data('lazy-load')) {
    const $lazyImages = $el.find('img[data-lazy]');
    $lazyImages.each(function lazyImages() {
      const $lazyImage = $(this);
      const imageSource = $lazyImage.attr('src');
      const dataSource = $lazyImage.data('lazy');
      const srcset = $lazyImage.data('srcset');
      if (imageSource !== dataSource) {
        $lazyImage.attr('src', dataSource);
      }

      if (srcset) {
        $lazyImage.attr('srcset', srcset);
      }
    });
  }
};
/**
 * Open Dropdown Handler
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const openDebounceHandler = ($el, $ddTrigger, $ddMenu, opts) => {
  if (!$el.hasClass(DD_DISABLED_STATE)) {
    openDropdown($el, $ddTrigger, $ddMenu, opts);
  }
};
/**
 * Set Aria Controls
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 */
const setAriaControls = ($ddTrigger, $ddMenu) => {
  if($ddMenu.length) {
    let ddMenuID = $ddMenu.attr('id');
    if(typeof ddMenuID !== typeof undefined && ddMenuID !== false) {
      $ddTrigger.attr('aria-controls', ddMenuID)
    } else {
      const randomNum = Math.ceil(Math.random() * 10000000000);
      const newMenuID = `dd-menu-${randomNum}`
      $ddTrigger.attr('aria-controls', newMenuID);
      $ddMenu.attr('id', newMenuID);
    }
  }
};
/**
 * Set Aria
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 */
const setA11y = ($el, $ddTrigger, $ddMenu) => {
  // If dropdown is an anchor add role="button" attribute
  if($ddTrigger.closest('a').length) {
    $ddTrigger.attr('role', 'button');
  }
  ariaExpanded($ddTrigger, false);
  setAriaControls($ddTrigger, $ddMenu);
};
/**
 * Set Aria
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 */
const offsetContent = ($el, $ddMenu) => {
  const menuWidth = $ddMenu.outerWidth();
  const btnOffset = $el.offset().left;
  const windowWidth = $(window).width();
  if((menuWidth + btnOffset) > windowWidth) {
    $ddMenu.css({ right: 0, left: 'auto' });
  } else if (menuWidth > btnOffset) {
    $ddMenu.css({ left: 0, right: 'auto'});
  }
};
/**
 * Prevent Hash
 * @param  {event} interaction event
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 */
const preventHash = (evt, $ddTrigger) => {
  if(Keyboard.parseKey(evt) === 'ENTER' || evt.type == 'click') {
    if($ddTrigger.attr('href') == "#") {
      evt.preventDefault();
    }
  }
};
/**
 * Set Event Listeners
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 * @param  {lodash} openDebounce - debounce for opening dropdown
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const closeDropdownOutside = ($el, $ddTrigger, $ddMenu, openDebounce, opts, evt) => {
  if (!$el.is(evt.target) && $el.has(evt.target).length === 0) {
    releaseTrapFocus($el);
    closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts)
  }
}
/**
 * Set Close Timeout
 * Some users accidentally leaves dropdown when entering the dropdown content.
 * This set timeout allows buffer time for user to reenter the dropdown
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 * @param  {lodash} openDebounce - debounce for opening dropdown
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const setCloseTimeout = ($el, $ddTrigger, $ddMenu, openDebounce, opts) => {
  openDebounce.cancel();
  $el.data('close-timeout', setTimeout(() => {
    closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts)
  }, 20));
}
/**
 * Clear set timeout for closing the dropdown
 * @param  {jQuery} $el - dropdown wrap
 */
const clearCloseTimeout = ($el) => {
  clearTimeout($el.data('close-timeout'));
}
/**
 * Set Event Listeners
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 * @param  {lodash} openDebounce - debounce for opening dropdown
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const setEventListeners = ($el, $ddTrigger, $ddMenu, openDebounce, opts) => {
  $el
    .on('mousemove.dropdown', openDebounce)
    .on('mouseleave.dropdown', () => setCloseTimeout($el, $ddTrigger, $ddMenu, openDebounce, opts))
    .on('mouseenter.dropdown', () => clearCloseTimeout($el))
    .on('keydown.dropdown', evt => {
      keydownHandler(evt, $el, $ddTrigger, $ddMenu, openDebounce, opts);
    })
    .on('click.dropdown', '.js-dropdown-close', evt => {
      closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts);
      focusOnTrigger($el);
      closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts);
    });

  $ddTrigger
    .on('click.dropdown', evt => {
      const isVoiceOverClick = evt.offsetX === 0 && evt.offsetY === 0 && evt.detail === 1;
      const isAnchor = evt.target.tagName === 'A';

      if (isAnchor) {
        // VoiceOver "click" on anchor, so prevent navigation to another page but handle dropdown
        if (isVoiceOverClick) {
          evt.preventDefault();
        }
        preventHash(evt, $ddTrigger);
      }
      keydownHandler(evt, $el, $ddTrigger, $ddMenu, openDebounce, opts);
    })
    .on('keydown.dropdown', evt =>
      preventHash(evt, $ddTrigger),
    );

    $(document).on('mouseup.dropdown', evt => {
      closeDropdownOutside($el, $ddTrigger, $ddMenu, openDebounce, opts, evt);
    });

    $(document).on('touchstart.dropdown', evt => {
      closeDropdownOutside($el, $ddTrigger, $ddMenu, openDebounce, opts, evt);
    });
    $(window).on('resize', throttle(() => {
      offsetContent($el, $ddMenu);
    }, 300));
};
/**
 * Open and Trap another dropdown
 * @param  {jQuery} $el - dropdown wrap
 * @param  {jQuery} $ddTrigger - dropdown trigger element
 * @param  {jQuery} $ddMenu - dropdown content element
 * @param  {jQuery} $returnFocus - return focus
 * @param  {Object} [options={}] - Any options provided upon initialization
 */
const openAndTrap = ($el, $ddTrigger, $ddMenu, $returnFocus, opts) => {
  openDebounceHandler($el, $ddTrigger, $ddMenu, opts)
  Keyboard.trapFocus($ddMenu);
  $el
  .addClass('trapped')
  .data('returnFocus', $returnFocus);
  const headingFocus = $($ddMenu.find('h2:visible').first());
  if(headingFocus){
    headingFocus.focus();
  }else{
    $ddTrigger.focus();
  }
};
/**
 * Initializes Header.
 * @param {HTMLElement} el - dropdown wrap
 * @param {Object} options - Options used for wiring up the modal
 * @see DEFAULTS
 */
export default function dropdown(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions(DEFAULTS, options, $el);
  const $ddMenu = $el.find(DD_MENU_CLASS).first();
  const $ddTrigger = $el.find(DD_TRIGGER_CLASS);
  const openDebounce = makeConditionalDebounce(
    () => openDebounceHandler($el, $ddTrigger, $ddMenu, opts),
    opts.wait,
    // condition: skip debounce if ANY dropdown is open
    () => $('.js-dropdown.dd-is-open').length > 0
  );

  setA11y($el, $ddTrigger, $ddMenu);

  if (opts.offsetContent) {
    offsetContent($el, $ddMenu);
  }
  setEventListeners($el, $ddTrigger, $ddMenu, openDebounce, opts);
  return {
    open: () => openDebounceHandler($el, $ddTrigger, $ddMenu, opts),
    openAndTrap: ($returnFocus) => openAndTrap($el, $ddTrigger, $ddMenu, $returnFocus, opts),
    close: () => closeDropdown($el, $ddTrigger, $ddMenu, openDebounce, opts),
  }
}

registerJQueryPlugin(PLUGIN_NAME, dropdown);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/dropdown.js